import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ActionClassificationCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/action-classification-command';
import { ConfluencePageCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-page-command';
import { ConfluenceUpdatePageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { EditorInsertContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { ReadContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import {
	RovoSendContextReadyCommand,
	RovoStreamAbortedCommand,
	RovoStreamCompletedCommand,
	RovoStreamErroredCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/streaming-lifecycle-commands';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import type {
	CommandConstructor,
	CommandProperties,
	CommandResult,
} from '@atlassian/rovo-content-bridge-api/command';

import { AbortCommandHandler } from './command-handlers/abort-command-handler';
import { ActionClassificationCommandHandler } from './command-handlers/action-classification-command-handler';
import type { ICommandHandler, PassThroughCommandProps } from './command-handlers/command-handler';
import { CreateCommandHandler } from './command-handlers/create-command-handler';
import { InsertContentCommandHandler } from './command-handlers/insert-content-command-handler';
import { PageUpdateCommandHandler } from './command-handlers/page-update-command-handler';
import type { PageUpdateStrategy } from './command-handlers/page-update-strategy';
import { ReadContentCommandHandler } from './command-handlers/read-content-command-handler';
import { SendContextReadyCommandHandler } from './command-handlers/send-context-ready-command-handler';
import { AdfUpdateStrategy } from './command-handlers/strategies/adf-update-strategy';
import { DynamicUiUpdateStrategy } from './command-handlers/strategies/dynamic-ui-update-strategy';
import { ImageUpdateStrategy } from './command-handlers/strategies/image-update-strategy';
import { StreamCompletedCommandHandler } from './command-handlers/stream-completed-command-handler';
import { StreamErroredCommandHandler } from './command-handlers/stream-errored-command-handler';
import { StreamingCommandHandler } from './command-handlers/streaming-command-handler';
import { UpdateCommandHandler } from './command-handlers/update-command-handler';
import { DocumentCreateCommandProcessor } from './stream-processors/create-command-processor';
import { DocumentUpdateCommandProcessor } from './stream-processors/update-command-processor';
import { DocumentDynamicUiUpdateCommandProcessor } from './stream-processors/update-dynamic-ui-command-processor';
import { DocumentImageUpdateCommandProcessor } from './stream-processors/update-image-command-processor';
import {
	AIFC_ADF_UPDATE_KEY,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
} from './types/command-manager';
import type { AddCommandHandlersFn, ICommandManager } from './types/command-manager';
import { log } from './utils/aifc-logging';

export class CommandManager implements ICommandManager {
	private commandHandlers: Map<
		CommandConstructor<CommandProperties, CommandResult>,
		ICommandHandler<CommandProperties, CommandResult>
	> = new Map();
	public readonly createCommandProcessor: DocumentCreateCommandProcessor;
	public readonly updateCommandProcessor: DocumentUpdateCommandProcessor;
	public readonly dynamicUiUpdateCommandProcessor?: DocumentDynamicUiUpdateCommandProcessor;
	public readonly imageUpdateCommandProcessor?: DocumentImageUpdateCommandProcessor;

	constructor(
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
		public commandProps: PassThroughCommandProps,
		private addCommandHandlers?: AddCommandHandlersFn | undefined,
	) {
		this.createCommandProcessor = new DocumentCreateCommandProcessor(
			this.commandProps.editorAPI,
			this.commandProps.contentLocation,
		);
		this.updateCommandProcessor = new DocumentUpdateCommandProcessor(
			this.commandProps.editorAPI,
			this.commandProps.contentLocation,
			this.clientId,
			this.rovoBridgeAPI,
			(params) => this.commandProps.onCwrRemixResultApplied?.(params),
			(replacement) => this.commandProps.onCwrRemixReplacementStarted?.(replacement),
			(replacement) => this.commandProps.onCwrRemixReplacementCancelled?.(replacement),
		);
		const onInlineRemixActioned: NonNullable<PassThroughCommandProps['onInlineRemixActioned']> = (
			params,
		) => this.commandProps.onInlineRemixActioned?.(params);
		this.dynamicUiUpdateCommandProcessor = new DocumentDynamicUiUpdateCommandProcessor(
			this.commandProps.editorAPI,
			onInlineRemixActioned,
		);
		this.imageUpdateCommandProcessor = new DocumentImageUpdateCommandProcessor(
			this.commandProps.onUploadAndInsertMedia,
			this.commandProps.editorAPI,
			onInlineRemixActioned,
		);
	}

	/**
	 * Products can use this method to add custom command handlers
	 * they can also add default command handlers using addDefaultCommandHandlers
	 *
	 * @param command - The command constructor
	 * @param handler - The command handler
	 * @returns The command manager
	 */
	addCommandHandler<
		P extends CommandProperties = CommandProperties,
		R extends CommandResult = CommandResult,
	>(command: CommandConstructor<P, R>, handler: ICommandHandler<P, R>): CommandManager {
		this.commandHandlers.set(
			command,
			handler as unknown as ICommandHandler<CommandProperties, CommandResult>,
		);
		return this;
	}

	/**
	 * Products can use this method to add default command handlers
	 * they can also add custom command handlers using addCommandHandler
	 *
	 * @returns The command manager
	 */
	addDefaultCommandHandlers(): CommandManager {
		this.addCreateCommandHandler();
		this.addUpdateCommandHandler();
		this.addActionClassificationCommandHandler();
		this.addReadCommandHandler();
		this.addAbortCommandHandler();
		this.addSendContextReadyCommandHandler();
		this.addRovoStreamCompletedCommandHandler();
		this.addRovoStreamErroredCommandHandler();
		this.addInsertContentCommandHandler();
		return this;
	}

	private registerCommandsToRovoBridge() {
		this.commandHandlers.forEach((handler, command) => {
			this.rovoBridgeAPI.on(this.clientId, command, (payload) => {
				return handler.execute(payload);
			});
		});
	}

	/**
	 * Add commands and register them to the rovo bridge
	 */
	addAndRegisterCommands(): void {
		if (this.addCommandHandlers) {
			this.addCommandHandlers({ commandManager: this });
		} else {
			this.addDefaultCommandHandlers();
		}
		this.registerCommandsToRovoBridge();
	}

	private deregisterCommandsFromRovoBridge() {
		this.commandHandlers.forEach((handler, command) => {
			this.rovoBridgeAPI.off(this.clientId, command);
			handler.deregister();
		});
	}

	/**
	 * Signals the image update processor to clear the AI generating state (e.g. on ACTION_ERROR).
	 */
	streamImageError(traceId?: string): void {
		this.imageUpdateCommandProcessor?.streamError('backend_action', traceId);
	}

	/**
	 * Deregister all commands
	 */
	deregisterCommands(): void {
		log('deregister commands');

		this.deregisterCommandsFromRovoBridge();
		this.commandHandlers.clear();
	}

	/**
	 * Update clientId
	 */
	updateClientId(clientId: ClientId, rovoBridgeAPI: RovoContentBridgeAPI): void {
		if (this.clientId === clientId && this.rovoBridgeAPI === rovoBridgeAPI) {
			return;
		}

		// add analytics event for the re-registration
		log('update clientId');

		this.deregisterCommandsFromRovoBridge();

		this.clientId = clientId;
		this.rovoBridgeAPI = rovoBridgeAPI;

		this.registerCommandsToRovoBridge();
	}

	/**
	 * Update commandProps
	 */
	updateCommandProps(commandProps: PassThroughCommandProps): void {
		if (this.commandProps === commandProps) {
			return;
		}

		log('update commandProps');

		this.commandProps = commandProps;
		this.commandHandlers.forEach((handler) => {
			handler.updateProps(commandProps);
		});

		if (this.imageUpdateCommandProcessor && commandProps.onUploadAndInsertMedia) {
			this.imageUpdateCommandProcessor.updateCallback(commandProps.onUploadAndInsertMedia);
		}
	}

	/**
	 * Update addCommandHandlers
	 */
	updateAddCommandHandlers(addCommandHandlers?: AddCommandHandlersFn): void {
		if (this.addCommandHandlers === addCommandHandlers) {
			return;
		}

		log('update addCommandHandlers');

		this.addCommandHandlers = addCommandHandlers;
		this.deregisterCommands();
		this.addAndRegisterCommands();
	}

	private addCreateCommandHandler() {
		const createCommandHandler = new CreateCommandHandler(
			this.commandProps,
			this.createCommandProcessor,
		);
		this.addCommandHandler(ConfluencePageCreateCommand, createCommandHandler);
	}

	private addUpdateCommandHandler() {
		if (
			this.dynamicUiUpdateCommandProcessor &&
			this.imageUpdateCommandProcessor &&
			expValEquals('cc-maui-experiment', 'isEnabled', true)
		) {
			const strategies = new Map<string, PageUpdateStrategy>([
				[AIFC_ADF_UPDATE_KEY, new AdfUpdateStrategy(this.updateCommandProcessor)],
				[DYNAMIC_UI_CREATE_KEY, new DynamicUiUpdateStrategy(this.dynamicUiUpdateCommandProcessor)],
				[IMAGE_CREATE_KEY, new ImageUpdateStrategy(this.imageUpdateCommandProcessor)],
			]);
			this.addCommandHandler(
				ConfluenceUpdatePageCommand,
				new PageUpdateCommandHandler(this.commandProps, strategies),
			);
		} else {
			this.addCommandHandler(
				ConfluenceUpdatePageCommand,
				new UpdateCommandHandler(this.commandProps, this.updateCommandProcessor),
			);
		}
	}

	private addActionClassificationCommandHandler() {
		this.addCommandHandler(
			ActionClassificationCommand,
			new ActionClassificationCommandHandler(this.commandProps, this.updateCommandProcessor),
		);
	}

	private addReadCommandHandler() {
		const readCommandHandler = new ReadContentCommandHandler(this.commandProps);
		this.addCommandHandler(ReadContentCommand, readCommandHandler);
	}

	private addAbortCommandHandler() {
		const abortCommandHandler = new AbortCommandHandler(this.commandProps, (command) => {
			// The token identifies the shared lifecycle, not a processor. Settle it
			// exactly before processor-local cleanup; stale terminal events stop here.
			if (command.inlineRovoEditAttemptId) {
				const wasSettled = this.imageUpdateCommandProcessor?.clearStreamingState(
					'generation-aborted',
					command.inlineRovoEditAttemptId,
				);
				if (!wasSettled) {
					return;
				}
			}
			this.commandHandlers.forEach((handler) => {
				if (handler instanceof StreamingCommandHandler) {
					handler.handleAbort('user_abort');
				}
			});
			this.dynamicUiUpdateCommandProcessor?.abort();
			if (!command.inlineRovoEditAttemptId) {
				this.imageUpdateCommandProcessor?.clearStreamingState('generation-aborted');
			}
			this.commandProps.onStreamAbort?.();
		});
		this.addCommandHandler(RovoStreamAbortedCommand, abortCommandHandler);
	}

	private addSendContextReadyCommandHandler() {
		const sendContextReadyHandler = new SendContextReadyCommandHandler(this.commandProps);
		this.addCommandHandler(RovoSendContextReadyCommand, sendContextReadyHandler);
	}

	private addRovoStreamCompletedCommandHandler() {
		const streamCompletedHandler = new StreamCompletedCommandHandler(
			this.commandProps,
			(command) => {
				if (command.inlineRovoEditAttemptId) {
					// Claimed processors retain ownership, including uploads that outlive the stream.
					this.commandProps.editorAPI?.malleableUi?.actions.settleUnclaimedInlineRovoEdit?.(
						command.inlineRovoEditAttemptId,
					);
				}
			},
		);
		this.addCommandHandler(RovoStreamCompletedCommand, streamCompletedHandler);
	}

	private addRovoStreamErroredCommandHandler() {
		const streamErroredHandler = new StreamErroredCommandHandler(this.commandProps, (command) => {
			// The token identifies the shared lifecycle, not a processor. Settle it
			// exactly before processor-local cleanup; stale terminal events stop here.
			if (command.inlineRovoEditAttemptId) {
				const wasSettled = this.imageUpdateCommandProcessor?.clearStreamingState(
					'generation-error',
					command.inlineRovoEditAttemptId,
				);
				if (!wasSettled) {
					return;
				}
			}
			this.dynamicUiUpdateCommandProcessor?.streamError();
			if (!command.inlineRovoEditAttemptId) {
				this.imageUpdateCommandProcessor?.clearStreamingState('generation-error');
			}
		});
		this.addCommandHandler(RovoStreamErroredCommand, streamErroredHandler);
	}

	private addInsertContentCommandHandler() {
		const insertContentCommandHandler = new InsertContentCommandHandler(this.commandProps);
		this.addCommandHandler(EditorInsertContentCommand, insertContentCommandHandler);
	}
}
